import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Container, Header, Spinner } from '@amzn/awsui-components-react';
import { Event, GetEventDetailsByEventIdCommandOutput, Source } from '@amzn/red-velvet-api';

import { useAllPagesRedVelvetQuery } from '../../../hooks/allPagesRedVelvetQuery';
import { useUser } from '../../../hooks/user';
import { canManageGivehubEvent, canManageVCEvent } from '../../../utils/shiftUtils';
import { EditEventLink } from '../../shifts/actions';
import { SpaLink } from '../../SpaLink';
import { useUrlPage } from '../../../hooks/attendance/urlPage';
import { FIRST_PAGE } from '../wizard/attendanceWizard';
import { useSearchParams } from 'react-router-dom';

import {
  DISPLAY_EVENT_ATTRIBUTES_TYPE,
  DISPLAY_EVENT_COORDINATOR_INFO,
  DISPLAY_EVENT_DATE_INFO,
  DISPLAY_EVENT_LOCATION_INFO,
  DISPLAY_EVENT_ORGANIZATION_INFO,
  DISPLAY_SHIFTS_COUNT_INFO,
  DISPLAY_TOTAL_SIGNUPS_INFO,
} from '../../events/eventInfo';
import { EventCard } from './eventCard';

interface EventAttendanceHeaderProps {
  event: Event;
  isEventFetched: boolean;
}

export function EventAttendanceHeader({ event, isEventFetched }: EventAttendanceHeaderProps) {
  const namespaces = ['attendance', 'translation'];
  const { t } = useTranslation(namespaces);
  const user = useUser();
  const { page, setPage } = useUrlPage(FIRST_PAGE);
  const [searchParams] = useSearchParams();
  const selectedTimezone = searchParams.get('timezone');
  const urlFeatures = searchParams.get('features');

  // We need to grab all detailed shifts to calculate total number of shifts and total signups for an event
  const { data: detailedShifts, isPending: isPendingDetailedShifts } = useAllPagesRedVelvetQuery({
    enabled: isEventFetched && !!event.eventId,
    // choosing 25 for shifts page size since this reduces the likely number of pagination calls necessary and
    // because 99.3% of GIVEhub events has 10 or fewer shifts confirmed by Gil, as of 12/15/2025 - cce-6307
    redVelvetQueryKey: ['getEventDetailsByEventId', { eventId: event.eventId, pageSize: 25 }],
    converter: (output: GetEventDetailsByEventIdCommandOutput) => ({
      items: output.detailedShifts ?? [],
      next: output.page,
    }),
  });

  const isLoading = !isEventFetched || isPendingDetailedShifts;

  if (isLoading) {
    return (
      <Container>
        <Spinner />
        <span>{t('fullEvent.loadingEvent')}</span>
      </Container>
    );
  }

  const isGivehub = event.source === Source.GIVEHUB;
  let headerAction;
  if (isGivehub && canManageGivehubEvent(event, user)) {
    headerAction = <EditEventLink event={event} source="trackAttendance" />;
  } else if (!isGivehub && canManageVCEvent(event, user) && page > 0) {
    headerAction = (
      <Button data-aci-analytics-name="trackAttendance_editEvent" onClick={() => setPage(0)}>
        {t('attendanceHeader.editEvent')}
      </Button>
    );
  }

  const viewDetailsUrl = `/view-event?id=${event.eventId}${selectedTimezone ? '&timezone=' + selectedTimezone : ''}${urlFeatures ? '&features=' + urlFeatures : ''}`;

  const displayAttributes: DISPLAY_EVENT_ATTRIBUTES_TYPE[] = [
    DISPLAY_EVENT_ORGANIZATION_INFO,
    DISPLAY_EVENT_COORDINATOR_INFO,
    DISPLAY_EVENT_LOCATION_INFO,
    DISPLAY_EVENT_DATE_INFO,
    DISPLAY_SHIFTS_COUNT_INFO,
    DISPLAY_TOTAL_SIGNUPS_INFO,
  ];

  return (
    <Container>
      <Header actions={headerAction}>
        {isGivehub ? (
          <SpaLink data-aci-analytics-name="trackAttendance_title" href={viewDetailsUrl}>
            <h2>{event.title}</h2>
          </SpaLink>
        ) : (
          <h2>{event.title}</h2>
        )}
      </Header>
      <EventCard
        event={event}
        detailedShifts={detailedShifts}
        displayAttributes={displayAttributes}
      />
    </Container>
  );
}
